import { useState } from 'react';
import { useNavigate } from 'react-router';
import { AlertTriangle, Pencil, Plus, Trash2 } from 'lucide-react';

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Pagination } from '@/components/ui/pagination';
import { Progress } from '@/components/ui/progress';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  useDeleteFundCost,
  useFundCosts,
  useFundCostsSummary,
} from '@/hooks/react-query/useFundCosts';
import { FUND_COST_CATEGORY_LABELS, type FundCost } from '@/types/fund-costs';
import { formatCurrency, formatDate } from '@/utils/formatters';

interface FundCostsSectionProps {
  fundId: string;
  fundCurrency: string;
  committedCapital: number;
  capPercentage?: number;
}

interface FundCostsTableProps {
  isLoading: boolean;
  costs: FundCost[];
  fundCurrency: string;
  onEdit: (cost: FundCost) => void;
  onDelete: (cost: FundCost) => void;
}

function FundCostsTable({
  isLoading,
  costs,
  fundCurrency,
  onEdit,
  onDelete,
}: FundCostsTableProps) {
  if (isLoading) {
    return (
      <div className="text-center py-8 text-muted-foreground">
        Loading costs...
      </div>
    );
  }
  if (costs.length === 0) {
    return (
      <div className="text-center py-8 text-muted-foreground">
        No cost transactions yet. Click &quot;Add Cost&quot; to record one.
      </div>
    );
  }
  return (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead>Description</TableHead>
          <TableHead>Category</TableHead>
          <TableHead>Counterparty</TableHead>
          <TableHead>From</TableHead>
          <TableHead>To</TableHead>
          <TableHead className="text-right">Amount</TableHead>
          <TableHead className="w-[80px]" />
        </TableRow>
      </TableHeader>
      <TableBody>
        {costs.map((cost) => (
          <TableRow key={cost.id}>
            <TableCell className="font-medium">
              {cost.description || '-'}
            </TableCell>
            <TableCell>
              <Badge variant="outline">
                {FUND_COST_CATEGORY_LABELS[cost.cost_category]}
              </Badge>
            </TableCell>
            <TableCell>{cost.counterparty_name ?? '-'}</TableCell>
            <TableCell>{formatDate(cost.effective_from)}</TableCell>
            <TableCell className="text-muted-foreground">
              {formatDate(cost.effective_to)}
            </TableCell>
            <TableCell className="text-right">
              {formatCurrency(cost.amount, { currency: fundCurrency })}
            </TableCell>
            <TableCell>
              {cost.transaction_status === 'DRAFT' && (
                <FundCostRowActions
                  onEdit={() => onEdit(cost)}
                  onDelete={() => onDelete(cost)}
                />
              )}
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
}

export function FundFeesFundsCosts({
  fundId,
  fundCurrency,
  committedCapital,
  capPercentage,
}: FundCostsSectionProps) {
  const navigate = useNavigate();
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const { data: costsPage, isLoading } = useFundCosts(fundId, {
    page,
    page_size: pageSize,
  });
  const { data: costsSummary } = useFundCostsSummary(fundId);
  const deleteMutation = useDeleteFundCost(fundId);

  const [deletingCost, setDeletingCost] = useState<FundCost | null>(null);

  const totalCosts = costsSummary?.total_costs ?? 0;
  const capAmount =
    capPercentage != null
      ? (capPercentage / 100) * committedCapital
      : undefined;
  const capUtilization =
    capAmount != null && capAmount > 0 ? (totalCosts / capAmount) * 100 : 0;
  const capExceeded = capUtilization >= 100;
  const costs = costsPage?.items ?? [];
  const totalCount = costsPage?.total_count ?? 0;

  const handlePageSizeChange = (newSize: number) => {
    setPageSize(newSize);
    setPage(1);
  };

  const handleDeleteConfirm = () => {
    if (!deletingCost) return;
    deleteMutation.mutate(deletingCost.id, {
      onSuccess: () => setDeletingCost(null),
    });
  };

  return (
    <div className="space-y-6">
      {/* Cap Summary */}
      {capPercentage != null && capAmount != null ? (
        <div className="space-y-1.5">
          <div className="flex items-center justify-between text-sm">
            <span className="text-muted-foreground">
              Fund Costs Cap ({capPercentage}%)
            </span>
            <div className="flex items-center gap-2">
              <span className="font-medium">
                {formatCurrency(totalCosts, { currency: fundCurrency })} /{' '}
                {formatCurrency(capAmount, { currency: fundCurrency })}
              </span>
              {capExceeded && (
                <Badge variant="outline" className="text-xs">
                  +
                  {formatCurrency(totalCosts - capAmount, {
                    currency: fundCurrency,
                  })}{' '}
                  over cap
                </Badge>
              )}
            </div>
          </div>
          <Progress value={Math.min(capUtilization, 100)} className="h-2" />
        </div>
      ) : (
        <div className="flex items-center gap-3 rounded-md border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-600">
          <AlertTriangle className="h-4 w-4 shrink-0" />
          <span>
            No operational costs cap defined. Set it in the fund&apos;s LPA
            Terms.
          </span>
          <span className="ml-auto font-medium">
            Total: {formatCurrency(totalCosts, { currency: fundCurrency })}
          </span>
        </div>
      )}

      {/* Transactions Table */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="text-lg">Cost Transactions</CardTitle>
            <Button
              onClick={() => navigate(`/app/funds/${fundId}/fund-costs/new`)}
              size="sm"
            >
              <Plus className="h-4 w-4 mr-2" />
              Add Cost
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          <FundCostsTable
            isLoading={isLoading}
            costs={costs}
            fundCurrency={fundCurrency}
            onEdit={(cost) =>
              navigate(`/app/funds/${fundId}/fund-costs/${cost.id}/edit`)
            }
            onDelete={setDeletingCost}
          />
          {totalCount > 0 && (
            <Pagination
              current={page}
              total={totalCount}
              pageSize={pageSize}
              onChange={setPage}
              onPageSizeChange={handlePageSizeChange}
              className="mt-4"
            />
          )}
        </CardContent>
      </Card>

      {/* Delete Confirmation */}
      <AlertDialog
        open={!!deletingCost}
        onOpenChange={(open) => !open && setDeletingCost(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Cost</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete{' '}
              {deletingCost?.description
                ? `"${deletingCost.description}"`
                : 'this cost'}{' '}
              (
              {formatCurrency(deletingCost?.amount ?? 0, {
                currency: fundCurrency,
              })}
              )? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteConfirm}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function FundCostRowActions({
  onEdit,
  onDelete,
}: {
  onEdit: () => void;
  onDelete: () => void;
}) {
  return (
    <div className="flex items-center gap-1">
      <Button
        variant="ghost"
        size="icon"
        className="h-8 w-8"
        aria-label="Edit cost"
        onClick={onEdit}
      >
        <Pencil className="h-4 w-4" />
      </Button>
      <Button
        variant="ghost"
        size="icon"
        className="h-8 w-8 text-destructive"
        aria-label="Delete cost"
        onClick={onDelete}
      >
        <Trash2 className="h-4 w-4" />
      </Button>
    </div>
  );
}
